import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

const STATUS = { PASS: "#22c55e", FAIL: "#ef4444",
                   FAIL_Mu_GT_LIM: "#ef4444", FAIL_STEEL_RATIO: "#f59e0b",
                   FAIL_DEFLECTION: "#ef4444" };

/** Ch 26.2 — Place columns/beams/slabs/footings, run IS 456 design checks. */
export default function StructuralElementsPanel({ projectId }) {
  const [palette, setPalette] = useState(null);
  const [model, setModel] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("columns");
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const [p, m, s] = await Promise.all([
        api.get(`/projects/${projectId}/structural-re/palette`),
        api.get(`/projects/${projectId}/structural-re/model`),
        api.get(`/projects/${projectId}/structural-re/design-summary`),
      ]);
      setPalette(p.data); setModel(m.data.model); setSummary(s.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  if (!model) return <div style={{ padding: 24, color: "var(--pf-muted)", fontSize: 12 }}>
    Loading…</div>;

  const xs = model.grid.x_axes || [];
  const ys = model.grid.y_axes || [];
  const lv = model.grid.levels || [];
  if (!xs.length || !ys.length || !lv.length) {
    return <div style={{ padding: 24, color: "var(--pf-muted)", fontSize: 12 }}>
      Define X-axes, Y-axes and levels first (Grid + Levels tab).</div>;
  }

  const addCol = async () => {
    try {
      await api.post(`/projects/${projectId}/structural-re/columns`,
        { type: "rcc-rect", axis_x: xs[0].label, axis_y: ys[0].label,
          from_level: lv[0].label, to_level: lv[lv.length - 1].label,
          b_mm: 400, d_mm: 400, P_u_kN: 1500, steel_pct: 1.5 });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const addBeam = async () => {
    if (xs.length < 2) return alert("Need ≥ 2 X-axes for a beam");
    try {
      await api.post(`/projects/${projectId}/structural-re/beams`,
        { type: "rcc-rect", level: lv[Math.min(1, lv.length - 1)].label,
          from_axis_x: xs[0].label, from_axis_y: ys[0].label,
          to_axis_x:   xs[1].label, to_axis_y:   ys[0].label,
          b_mm: 300, d_mm: 500, w_kn_per_m: 25, steel_pct: 1.0 });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const addSlab = async () => {
    if (xs.length < 2 || ys.length < 2)
      return alert("Need ≥ 2 X-axes and 2 Y-axes for a slab");
    try {
      await api.post(`/projects/${projectId}/structural-re/slabs`,
        { type: "two-way", level: lv[Math.min(1, lv.length - 1)].label,
          corner_axes: [[xs[0].label, ys[0].label], [xs[1].label, ys[0].label],
                          [xs[1].label, ys[1].label], [xs[0].label, ys[1].label]],
          th_mm: 150 });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const addFooting = async () => {
    if (!model.columns?.length) return alert("Add a column first");
    try {
      await api.post(`/projects/${projectId}/structural-re/footings`,
        { type: "isolated", column_id: model.columns[0].id,
          B_m: 2.0, L_m: 2.0, P_kN: 1500, qa_kpa: 200 });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const removeItem = async (kind, id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/projects/${projectId}/structural-re/${kind}/${id}`);
    reload();
  };

  const tabs = [
    { key: "columns", label: `Columns (${model.columns.length})`, items: model.columns,
       results: summary?.columns || [], add: addCol, kind: "columns" },
    { key: "beams",   label: `Beams (${model.beams.length})`,     items: model.beams,
       results: summary?.beams || [],   add: addBeam, kind: "beams" },
    { key: "slabs",   label: `Slabs (${model.slabs.length})`,     items: model.slabs,
       results: summary?.slabs || [],   add: addSlab, kind: "slabs" },
    { key: "footings",label: `Footings (${model.footings.length})`, items: model.footings,
       results: summary?.footings || [],add: addFooting, kind: "footings" },
  ];
  const cur = tabs.find((t) => t.key === tab);

  return (
    <div data-testid="structural-elements" style={{ display: "grid",
            gridTemplateColumns: "1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
          {tabs.map((t) => (
            <button key={t.key} data-testid={`se-tab-${t.key}`}
              style={tab === t.key ? btn : btnGhost}
              onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </div>
        <button data-testid={`se-add-${cur.key}`} style={{ ...btn, marginBottom: 12 }}
          onClick={cur.add}>+ Add {cur.key.slice(0, -1)}</button>

        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.05)", textAlign: "left" }}>
              <th style={{ padding: 6 }}>id</th>
              <th style={{ padding: 6 }}>type</th>
              <th style={{ padding: 6 }}>geometry</th>
              <th style={{ padding: 6 }}>design check</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cur.items.map((it) => {
              const r = cur.results.find((rr) => rr[`${cur.key.slice(0,-1)}_id`] === it.id) || {};
              const status = r.capacity_status || r.moment_status || r.deflection_status || r.bearing_status;
              const colour = STATUS[status] || "#888";
              return (
                <tr key={it.id} data-testid={`se-row-${it.id}`}
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.06)",
                              borderLeft: `3px solid ${colour}` }}>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>{it.id}</td>
                  <td style={{ padding: 6 }}>{it.type}</td>
                  <td style={{ padding: 6, fontSize: 10, fontFamily: "monospace" }}>
                    {cur.key === "columns" && `${it.b_mm}×${it.d_mm}mm @ ${it.axis_x}-${it.axis_y}`}
                    {cur.key === "beams" && `${it.b_mm}×${it.d_mm}mm L${(r.span_m || "?").toFixed?.(1) || "?"}m @ ${it.from_axis_x}${it.from_axis_y}→${it.to_axis_x}${it.to_axis_y}`}
                    {cur.key === "slabs" && `${it.th_mm}mm thick · ${it.type}`}
                    {cur.key === "footings" && `${it.B_m}×${it.L_m}m`}
                  </td>
                  <td style={{ padding: 6, fontSize: 10 }}>
                    {cur.key === "columns" && r.P_cap_kN &&
                      <span style={{ color: colour, fontWeight: 600 }}>
                        P_cap {r.P_cap_kN}kN vs P_u {r.P_u_kN}kN · {r.capacity_status}
                      </span>}
                    {cur.key === "beams" && r.Mu_kNm &&
                      <span style={{ color: colour, fontWeight: 600 }}>
                        M_u {r.Mu_kNm} vs lim {r.Mu_lim_kNm} · {r.moment_status}
                      </span>}
                    {cur.key === "slabs" && r.Ld_actual &&
                      <span style={{ color: colour, fontWeight: 600 }}>
                        L/d {r.Ld_actual} vs max {r.Ld_max} · {r.deflection_status}
                      </span>}
                    {cur.key === "footings" && r.q_actual_kpa &&
                      <span style={{ color: colour, fontWeight: 600 }}>
                        q {r.q_actual_kpa} vs q_a {r.qa_kpa} · {r.bearing_status}
                      </span>}
                  </td>
                  <td><button data-testid={`se-del-${it.id}`}
                                style={{ ...btnGhost, padding: "1px 5px", fontSize: 10 }}
                                onClick={() => removeItem(cur.kind, it.id)}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={label}>Design totals</div>
        {summary && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            {[
              ["Columns", summary.totals.columns],
              ["Beams", summary.totals.beams],
              ["Slabs", summary.totals.slabs],
              ["Shear walls", summary.totals.shear_walls],
              ["Footings", summary.totals.footings],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between",
                                       padding: "4px 8px", background: "rgba(255,255,255,0.04)",
                                       borderRadius: 3 }}>
                <span style={{ fontSize: 11 }}>{k}</span>
                <span style={{ fontFamily: "monospace", color: "#F26B1F", fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ ...label, marginTop: 18 }}>IS 456 references</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          Columns: Cl 39 (axial capacity = 0.4·fck·Ag + 0.67·fy·Asc).<br />
          Beams: Cl 26 + Annex G (M_u ≤ M_u,lim with Fe 500 xu/d 0.456).<br />
          Slabs: Cl 23.2.1 (L/d basic 20 simply, 26 continuous, 7 cantilever, ×1.3–1.5 for two-way).<br />
          Footings: bearing pressure q ≤ q_a.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
